import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { LuCheck } from 'react-icons/lu'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { getStoredUserProfile } from '../utils/authStorage'
import { buildApiUrl } from '../lib/api'
import { fetchResolvedUserPlan } from '../utils/planApi'
import { resolveUserIdentity } from '../utils/userIdentity'
import useLocale from '../hooks/useLocale'
import '../styles/Layout.css'
import '../styles/UpgradePage.css'

const PRO_PRICE_YEN = (() => {
  const raw = process.env.REACT_APP_PRO_PLAN_AMOUNT_YEN || '500'
  const parsed = Number.parseInt(raw, 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 500
})()

const FREE_PLAN_SCORE_LIMIT = (() => {
  const raw = process.env.REACT_APP_FREE_PLAN_SCORE_LIMIT || '3'
  const parsed = Number.parseInt(raw, 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 3
})()

const UPGRADE_TEXT = {
  ja: {
    kicker: 'PRO PLAN',
    title: 'PROプランへアップグレード',
    subtitle: '保存スコア上限の拡張と広告の非表示を利用できます。',
    freeTag: 'FREE',
    proTag: 'PRO',
    freeTitle: 'Chord Analyzer FREE',
    proTitle: 'Chord Analyzer PRO',
    freeDescription: `無料プランでは保存スコアは${FREE_PLAN_SCORE_LIMIT}件までです。`,
    proDescription: '保存スコア無制限、広告非表示の買い切りプランです。',
    freeFeatures: [
      `保存スコア ${FREE_PLAN_SCORE_LIMIT}件まで`,
      '広告表示あり',
    ],
    proFeatures: [
      '保存スコア 無制限',
      '広告非表示',
    ],
    nowUsing: '現在このプランを利用中です。',
    checkoutButton: 'PROプランを利用する',
    checkoutLoading: '決済ページへ移動中...',
    checkoutError: '決済ページの作成に失敗しました。しばらくしてから再度お試しください。',
    checkoutNotConfigured: '決済機能の設定が未完了です（Stripe Secret Key未設定）。管理者設定後に再度お試しください。',
    checkoutRouteMissing: '決済APIが未デプロイです。バックエンドを最新化してください。',
    loadPlanError: 'プラン状態の取得に失敗しました。',
    checkoutSuccess: '決済が完了しました。PROプランを有効化しています。',
    checkoutCancel: '決済はキャンセルされました。',
    alreadyPro: 'すでにPROプランをご利用中です。',
    freePlanPassive: '無料プラン',
    refresh: '再読み込み',
  },
  en: {
    kicker: 'PRO PLAN',
    title: 'Upgrade to PRO',
    subtitle: 'Unlock more saved scores and remove ads.',
    freeTag: 'FREE',
    proTag: 'PRO',
    freeTitle: 'Chord Analyzer FREE',
    proTitle: 'Chord Analyzer PRO',
    freeDescription: `The FREE plan allows up to ${FREE_PLAN_SCORE_LIMIT} saved scores.`,
    proDescription: 'A one-time upgrade with unlimited saved scores and no ads.',
    freeFeatures: [
      `Up to ${FREE_PLAN_SCORE_LIMIT} saved scores`,
      'Ads shown',
    ],
    proFeatures: [
      'Unlimited saved scores',
      'No ads',
    ],
    nowUsing: 'You are currently using this plan.',
    checkoutButton: 'Upgrade to PRO',
    checkoutLoading: 'Redirecting to Checkout...',
    checkoutError: 'Failed to create Checkout session. Please try again later.',
    checkoutNotConfigured: 'Checkout is not configured yet (Stripe secret key is missing).',
    checkoutRouteMissing: 'Checkout API is not deployed yet. Please deploy backend updates.',
    loadPlanError: 'Failed to load current plan.',
    checkoutSuccess: 'Payment completed. Activating your PRO plan.',
    checkoutCancel: 'Checkout was canceled.',
    alreadyPro: 'You are already on PRO.',
    freePlanPassive: 'Free plan',
    refresh: 'Refresh',
  },
}

function formatYen(locale, amount) {
  const normalizedLocale = locale === 'ja' ? 'ja-JP' : 'en-US'
  try {
    return new Intl.NumberFormat(normalizedLocale, {
      style: 'currency',
      currency: 'JPY',
      maximumFractionDigits: 0,
    }).format(amount)
  } catch (_error) {
    return `¥${amount}`
  }
}

function getQueryParam(paramName) {
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(window.location.search)
  return params.get(paramName)
}

export default function UpgradePage({ user: userProp }) {
  const locale = useLocale()
  const text = UPGRADE_TEXT[locale] || UPGRADE_TEXT.en
  const fallbackUser = useMemo(() => getStoredUserProfile(), [])
  const user = userProp || fallbackUser
  const identity = resolveUserIdentity(user)
  const { email, userId } = identity
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [plan, setPlan] = useState('FREE')
  const [isLoadingPlan, setIsLoadingPlan] = useState(true)
  const [isStartingCheckout, setIsStartingCheckout] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [noticeMessage, setNoticeMessage] = useState('')

  const fetchPlan = useCallback(async () => {
    if (!userId) {
      setPlan('FREE')
      setIsLoadingPlan(false)
      return
    }

    try {
      setIsLoadingPlan(true)
      setErrorMessage('')
      const nextPlan = await fetchResolvedUserPlan({ email, userId })
      setPlan(nextPlan)
    } catch (error) {
      console.error('Failed to fetch plan', error)
      setErrorMessage(text.loadPlanError)
      setPlan('FREE')
    } finally {
      setIsLoadingPlan(false)
    }
  }, [email, text.loadPlanError, userId])

  useEffect(() => {
    fetchPlan()
  }, [fetchPlan])

  useEffect(() => {
    const checkoutResult = getQueryParam('checkout')
    if (checkoutResult === 'success') {
      setNoticeMessage(text.checkoutSuccess)
      fetchPlan()
    } else if (checkoutResult === 'cancel') {
      setNoticeMessage(text.checkoutCancel)
    }
  }, [fetchPlan, text.checkoutCancel, text.checkoutSuccess])

  const handleCheckout = async () => {
    if (!userId || isStartingCheckout) return

    setIsStartingCheckout(true)
    setErrorMessage('')
    setNoticeMessage('')

    try {
      const response = await fetch(buildApiUrl('/create-checkout-session'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          email: user?.email || '',
          locale,
        }),
      })
      const data = await response.json().catch(() => ({}))
      const backendMessage = String(data?.message || data?.error || '')
      if (!response.ok) {
        if (response.status === 403 && /missing authentication token/i.test(backendMessage)) {
          setErrorMessage(text.checkoutRouteMissing)
          return
        }
        if (/stripe secret key is not configured/i.test(backendMessage)) {
          setErrorMessage(text.checkoutNotConfigured)
          return
        }
        setErrorMessage(
          backendMessage
            ? `${text.checkoutError} (${backendMessage})`
            : text.checkoutError
        )
        return
      }

      if (data.alreadyPro) {
        setPlan('PRO')
        setNoticeMessage(text.alreadyPro)
        return
      }

      if (!data.checkoutUrl) {
        throw new Error('Missing checkoutUrl')
      }

      window.location.href = data.checkoutUrl
    } catch (error) {
      console.error('Failed to start checkout', error)
      setErrorMessage(text.checkoutError)
    } finally {
      setIsStartingCheckout(false)
    }
  }

  const isPro = plan === 'PRO'
  const priceLabel = formatYen(locale, PRO_PRICE_YEN)

  return (
    <div className="page-shell">
      <Header user={user} isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      <main className="page-main upgrade-main">
        <section className={`upgrade-shell ${isMenuOpen ? 'shift-right' : ''}`}>
          <div className="upgrade-hero">
            <p className="upgrade-kicker">{text.kicker}</p>
            <h1 className="upgrade-title">{text.title}</h1>
            <p className="upgrade-subtitle">{text.subtitle}</p>
          </div>

          {noticeMessage && <div className="upgrade-notice">{noticeMessage}</div>}
          {errorMessage && <div className="upgrade-error">{errorMessage}</div>}

          <div className="upgrade-grid">
            <article className="upgrade-card upgrade-card--free">
              <span className="upgrade-card-badge">{text.freeTag}</span>
              <h2 className="upgrade-card-title">{text.freeTitle}</h2>
              <p className="upgrade-card-description">{text.freeDescription}</p>
              <p className="upgrade-price">¥0</p>
              <ul className="upgrade-feature-list">
                {text.freeFeatures.map((item) => (
                  <li className="upgrade-feature-item" key={item}>
                    <LuCheck className="upgrade-feature-icon" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="upgrade-plan-state">
                {!isPro ? text.nowUsing : text.freePlanPassive}
              </div>
            </article>

            <article className="upgrade-card upgrade-card--pro">
              <span className="upgrade-card-badge upgrade-card-badge--pro">{text.proTag}</span>
              <h2 className="upgrade-card-title">{text.proTitle}</h2>
              <p className="upgrade-card-description">{text.proDescription}</p>
              <p className="upgrade-price">{priceLabel}</p>
              <ul className="upgrade-feature-list">
                {text.proFeatures.map((item) => (
                  <li className="upgrade-feature-item" key={item}>
                    <LuCheck className="upgrade-feature-icon" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className="upgrade-checkout-button"
                disabled={isPro || isStartingCheckout || isLoadingPlan}
                onClick={handleCheckout}
              >
                {isPro ? text.nowUsing : (isStartingCheckout ? text.checkoutLoading : text.checkoutButton)}
              </button>
            </article>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}
